import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X } from 'lucide-react';

interface MenuSearchProps {
  value: string;
  onChange: (query: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  searchHistory?: string[];
  popularSearches?: string[];
  isSearching?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * MenuSearch - Advanced search component with debounced input, history, and suggestions
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const MenuSearch: React.FC<MenuSearchProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder = "Search menu items...",
  searchHistory = [], // Popover functionality removed as per requirements
  popularSearches = [], // Popover functionality removed as per requirements
  isSearching = false,
  disabled = false,
  className = "",
}) => {
  // Suppress linting for removed functionality parameters
  void searchHistory; void popularSearches;
  
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Sync local value with prop value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounce the onChange call
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [localValue, onChange, value]);

  const handleFocus = useCallback(() => {
    if (disabled) return;
    setIsFocused(true);
    onFocus?.();
  }, [onFocus, disabled]);

  const handleBlur = useCallback(() => {
    if (disabled) return;
    setIsFocused(false);
    onBlur?.();
  }, [onBlur, disabled]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    setLocalValue(e.target.value);
  }, [disabled]);

  const handleClear = useCallback(() => {
    if (disabled) return;
    setLocalValue('');
    onChange('');
  }, [onChange, disabled]);

  return (
    <div className={`relative ${className}`}>
      {/* Search Input */}
      <div className={`
        relative flex items-center bg-white border rounded-md transition-all duration-200
        ${disabled 
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
          : isFocused 
            ? 'ring-1' 
            : 'border-gray-300 hover:border-gray-300'
        }
      `}
      style={{
        borderColor: isFocused ? 'var(--theme-color)' : '#D1D5DB',
        ...(isFocused && { '--tw-ring-color': 'var(--theme-color)' } as React.CSSProperties)
      }}
      >
        <Search className={`
          w-5 h-5 absolute left-3 transition-colors duration-200
          ${disabled 
            ? 'text-gray-300'
            : isFocused 
              ? '' 
              : 'text-gray-400'
          }
        `} 
        style={isFocused && !disabled ? { color: 'var(--theme-color)' } : {}}
        />
        
        <input
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full pl-11 pr-12 py-3 bg-transparent text-gray-900 placeholder-gray-500 
            focus:outline-none text-base
            ${disabled ? 'cursor-not-allowed text-gray-400' : ''}
          `}
        />

        {/* Loading or Clear Button */}
        <div className="absolute right-3 flex items-center space-x-2">
          {isSearching && (
            <div 
              className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" 
              style={{ borderColor: 'var(--theme-color)', borderTopColor: 'transparent' }}
            />
          )}
          
          {localValue && !isSearching && !disabled && (
            <button
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Search Suggestions - Removed as per requirement */}
    </div>
  );
};